import { useEffect, useRef, useState } from 'react'
import { ArrowUpDown, Check } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { KEY_SORTS, type KeySort } from './key-sort.js'

/** "Sort: Name ▾" beside the keybook's search — the drive's sort menu, as a labelled pill. */
export function KeySortMenu({ sort, onChange }: { sort: KeySort; onChange: (sort: KeySort) => void }) {
  const { t } = useTranslation('keybook')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('click', onDocClick, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDocClick, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="kbg-btn h-8 px-3 rounded-full text-sm flex items-center gap-1.5"
        title={t('rack.sortTitle')}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="keybook-sort"
      >
        <ArrowUpDown size={13} aria-hidden />
        {t('rack.sortBy', { field: t(`rack.sort.${sort}`) })}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-9 ws-menu z-20 min-w-[180px]">
          {KEY_SORTS.map(s => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={s === sort}
              onClick={() => { onChange(s); setOpen(false) }}
              className="ws-menu-item"
              data-testid={`keybook-sort-${s}`}
            >
              <span className="flex-1 text-left">{t(`rack.sort.${s}`)}</span>
              {s === sort && <Check size={12} aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
